<template>
  <section
    :class="['pricing', (shoppingCart.length >= 2 || isPromoAvailable) && 'pricing--populated']"
    id="pricing"
  >
    <div class="pricing__title-wrapper">
      <h3 class="pricing__title">Cart</h3>
    </div>
    <div class="pricing__breakdown">
      <div v-if="subscriptionsList.length" class="pricing__products-section">
        <PricingListItem
          v-for="product in subscriptionsList"
          :key="product.id"
          :product="product"
        />
      </div>
      <div
        v-if="certificationsList.length"
        class="pricing__products-section pricing__products-section--certifications"
      >
        <PricingListItem
          v-for="product in certificationsList"
          :key="product.id"
          :product="product"
        />
      </div>
      <div
        v-if="liveVirtualTrainingsInCart.length"
        class="pricing__products-section pricing__products-section--live-virtual-trainings"
      >
        <PricingListItem
          v-for="product in liveVirtualTrainingsInCart"
          :key="product.id"
          :product="product"
        />
      </div>
      <div
        v-if="productBundleInCart"
        class="pricing__products-section pricing__products-section--product-bundles"
      >
        <PricingListItemProductBundle
          :key="productBundleInCart.id"
          :product="productBundleInCart"
        />
      </div>
      <AddOns v-if="certificationsInAddOns.length" :certifications="certificationsInAddOns" />
      <div class="pricing__prices-wrapper">
        <PricingListPromoCode @coupon-discount="setCouponDiscount" />
        <PricingListPriceDetails />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { priceDetailUpdates, updateCart, promoProductBundleUtils } from '@/helpers/mixins'
import PricingListItem from '@/components/cart/pricing-list/PricingListItem.vue'
import PricingListItemProductBundle from '@/components/cart/pricing-list/PricingListItemProductBundle.vue'
import PricingListPriceDetails from '@/components/cart/pricing-list/PricingListPriceDetails.vue'
import PricingListPromoCode from '@/components/cart/pricing-list/PricingListPromoCode.vue'
import AddOns from '@/components/cart/add-ons/AddOns.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'PricingList',
  mixins: [priceDetailUpdates, updateCart, promoProductBundleUtils],
  components: {
    PricingListItem,
    PricingListItemProductBundle,
    PricingListPriceDetails,
    PricingListPromoCode,
    AddOns,
  },
  data() {
    return {
      certificationsList: [],
      certificationsInAddOns: [],
      subscriptionsList: [],
      currentDiscount: 0,
    }
  },
  computed: {
    ...mapState({
      availableCertifications: state => state.products.availableCertifications,
      availablePlans: state => state.products.availablePlans,
      availableLiveVirtualTrainings: state => state.products.availableLiveVirtualTrainings,
      availableProductBundles: state => state.products.availableProductBundles,
    }),
    ...mapGetters({
      isBusinessUser: 'business/isBusinessUser',
    }),
    subscriptionsInCart() {
      const codes = this.availablePlans.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    certificationsInCart() {
      const codes = this.availableCertifications.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id))
    },
    liveVirtualTrainingsInCart() {
      const codes = this.availableLiveVirtualTrainings.map(p => p.merchant_id)
      return this.shoppingCart.filter(i => codes.includes(i.merchant_id) && !i.addOn)
    },
    productBundleInCart() {
      const codes = this.availableProductBundles.map(p => p.slug)
      return this.shoppingCart.find(i => codes.includes(i.merchant_id) && !i.addOn)
    },
  },
  methods: {
    setSubscriptionsList() {
      const { PLANS } = CONSTANTS
      const codes = this.availablePlans.map(p => p.merchant_id)
      const subscriptionsList = []
      this.shoppingCart.forEach(i => {
        if (codes.includes(i.merchant_id) && i.merchant_id === PLANS.SKILL_DIVE_ANNUAL) {
          if (!i.selectedAsAddOns) subscriptionsList.push(i)
        } else if (codes.includes(i.merchant_id)) {
          subscriptionsList.push(i)
        }
      })
      this.subscriptionsList = subscriptionsList
    },
    setCertificationsList() {
      const { PLANS, VOUCHERS } = CONSTANTS
      const itemsId = this.shoppingCart.map(item => item.merchant_id)
      this.setCertificationsInAddOns()
      const addOnsItemsId = this.certificationsInAddOns.map(item => item.merchant_id)
      this.certificationsList = this.availableCertifications.filter(item => {
        if (this.selectedPlanCode === PLANS.ANNUAL && item.merchant_id === VOUCHERS.ICCA) {
          this.removeSelectedFromShoppingCart(item)
          return false
        }
        return this.isCartFlowPage
          ? itemsId.includes(item.merchant_id) && !item.addOn
          : itemsId.includes(item.merchant_id) && !addOnsItemsId.includes(item.merchant_id)
      })
    },
    setCertificationsInAddOns() {
      const { PLANS, VOUCHERS } = CONSTANTS
      const itemsId = this.shoppingCart.map(item => item.merchant_id)

      this.certificationsInAddOns = this.availableCertifications
        .filter(item => {
          item.included = false
          switch (item.merchant_id) {
            case VOUCHERS.ICCA:
              if ([PLANS.ANNUAL].includes(this.selectedPlanCode)) item.included = true
              break
            case VOUCHERS.EJPT:
              if (
                [PLANS.ANNUAL, PLANS.MONTHLY_EJPT, PLANS.QUARTERLY_EJPT].includes(
                  this.selectedPlanCode
                )
              )
                item.included = true
              break
            case VOUCHERS.EDA:
              if ([PLANS.EDA_PREMIUM].includes(this.selectedPlanCode)) item.included = true
              break
            case VOUCHERS.EWPT:
              if ([PLANS.EWPT_PREMIUM].includes(this.selectedPlanCode)) item.included = true
              break
            default:
              break
          }
          if (
            !this.isCartFlowPage &&
            !this.shoppingCart.find(i => i.merchant_id === item.merchant_id)?.addOn
          )
            item.addOnChecked = false
          return !itemsId.includes(item.merchant_id) || item.addOn
        })
        .reverse()
    },
    skillDiveItemReset() {
      const plan = this.shoppingCart.find(
        item => item.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL
      )
      if (plan && Object.keys(plan).includes('asAddOns')) plan.asAddOns = false
      if (plan && Object.keys(plan).includes('recurlySDLACounter')) plan.recurlySDLACounter = 0
      if (plan && Object.keys(plan).includes('recurlySDItem')) plan.recurlySDItem = false
    },
    setCouponDiscount(value) {
      this.currentDiscount = value
    },
  },
  watch: {
    shoppingCart(items) {
      this.calculatePricing()
    },
    paymentEntries: {
      deep: true,
      handler(paymentEntries) {
        const { country, zip } = paymentEntries
        this.recurlyPricing
          .address({
            country,
            postal_code: zip,
          })
          .catch(err => this.handleError(err))
          .done()
      },
    },
    subscriptionsInCart() {
      this.setSubscriptionsList()
    },
    certificationsInCart() {
      this.setCertificationsList()
    },
  },
  created() {
    this.isPromoAvailable = this.coupon.length > 0
    this.skillDiveItemReset()
    this.shoppingCart.forEach(item => {
      if (item && Object.keys(item).includes('recurlyItem')) item.recurlyItem = false
    })
    this.setSubscriptionsList()
    this.setCertificationsList()
    this.setCertificationsInAddOns()
    this.calculatePricing()
  },
}
</script>

<style lang="scss">
.pricing {
  height: 100%;
  width: 100%;
  margin: 0 auto;
  &__title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 20px 0;
  }
  &__title {
    color: var(--white);
    font-family: var(--font-regular);
    font-weight: 450;
    font-size: rem(24);
    line-height: 32px;
    margin: 0;
  }
  &__count-badge {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    padding: 9px 14px;
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 20px;
    text-transform: uppercase;
    font-size: rem(12);
    line-height: 14px;
    font-family: var(--font-regular);
  }
  &__breakdown {
    padding: 0;
  }
  &__prices-wrapper {
    position: relative;
    margin: 20px 0 0 0;
    @include tablet {
      margin: 50px 0 0 0;
      margin: 0;
    }
  }
  &__input {
    background: #fff;
    padding: 5px;
    margin: 0;
    width: 100%;
    border-radius: 5px;
    font-size: 1rem;
    border: 1px solid var(--faded-gray);
    opacity: 0.7;
    font-family: var(--font-regular);
    &:focus {
      opacity: 1;
    }
  }
  &__products {
    &-section {
      display: flex;
      flex-flow: column;
      row-gap: 10px;
      &--certifications,
      &--live-virtual-trainings,
      &--product-bundles {
        margin-top: 10px;
        .pricing-list-item {
          &__wrapper {
            height: auto;
            align-items: center;
          }
          &__btn {
            align-self: auto;
          }
        }
      }
    }
  }
}
</style>
